import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

const EVT = "lf-storage";
const read = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};
const write = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(EVT, { detail: key }));
};

export function useStored(key, fallback) {
  const [value, setValue] = useState(() => read(key, fallback));
  useEffect(() => {
    const sync = (e) => { if (!e.detail || e.detail === key) setValue(read(key, fallback)); };
    window.addEventListener(EVT, sync);
    return () => window.removeEventListener(EVT, sync);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = useCallback((v) => write(key, typeof v === "function" ? v(read(key, fallback)) : v), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return [value, set];
}

export function useFavorites() {
  const [favs, setFavs] = useStored("lf_favorites", []);
  const toggle = useCallback((id) => setFavs((f) => (f.includes(id) ? f.filter((x) => x !== id) : [id, ...f])), [setFavs]);
  return { favs, toggle, isFav: (id) => favs.includes(id) };
}

export const pushRecent = (id) => write("lf_recent", [id, ...read("lf_recent", []).filter((x) => x !== id)].slice(0, 8));
export const useRecent = () => useStored("lf_recent", [])[0];

export const useConsent = () => useStored("lf_consent", null);

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

export function captureAttribution() {
  if (sessionStorage.getItem("lf_attr")) return;
  const p = new URLSearchParams(window.location.search);
  const attr = { referrer: document.referrer || null, landing_page: window.location.pathname + window.location.search };
  UTM_KEYS.forEach((k) => { if (p.get(k)) attr[k] = p.get(k).slice(0, 200); });
  sessionStorage.setItem("lf_attr", JSON.stringify(attr));
}

export const getAttribution = () => {
  try { return JSON.parse(sessionStorage.getItem("lf_attr")) || {}; } catch { return {}; }
};

const sessionId = () => {
  let s = sessionStorage.getItem("lf_sid");
  if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("lf_sid", s); }
  return s;
};

export function track(event, extra = {}) {
  if (read("lf_consent", null) !== "accepted") return;
  const a = getAttribution();
  const utm = Object.fromEntries(UTM_KEYS.filter((k) => a[k]).map((k) => [k, a[k]]));
  api.post("/track", { event, path: window.location.pathname, session_id: sessionId(), referrer: a.referrer, lang: localStorage.getItem("lf_lang") || "en", utm, ...extra }).catch(() => {});
}
